import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Share, Mail, Download, Calendar, FileText } from 'lucide-react';
import { toast } from "@/hooks/ui/use-toast";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format, startOfMonth, endOfMonth, isWithinInterval, addMonths, subMonths } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { DayShift, Employee } from '@/types';

interface ShareCalendarDialogProps {
  currentDisplayMonth: Date;
  businessName?: string;
  calendarRef?: React.RefObject<HTMLElement>;
  onSyncCalendar?: () => void;
  shifts?: DayShift[]; // Shift data for CSV/Excel export
  employees?: Employee[]; // Employee data for exports
  trigger?: React.ReactNode; // Optional custom trigger element
}

const ShareCalendarDialog: React.FC<ShareCalendarDialogProps> = ({
  currentDisplayMonth,
  businessName = 'Your Business',
  calendarRef,
  onSyncCalendar,
  shifts = [],
  employees = [],
  trigger
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    recipients: '',
    subject: `${businessName} - Shift Schedule for ${format(currentDisplayMonth, 'MMMM yyyy')}`,
    message: `Please find attached the shift schedule for ${format(currentDisplayMonth, 'MMMM yyyy')}.`
  });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // Download filtering options
  const [downloadRange, setDownloadRange] = useState<'current-month' | 'next-month' | 'custom-range' | 'all-data'>('current-month');
  const [customStartDate, setCustomStartDate] = useState<string>(format(startOfMonth(currentDisplayMonth), 'yyyy-MM-dd'));
  const [customEndDate, setCustomEndDate] = useState<string>(format(endOfMonth(currentDisplayMonth), 'yyyy-MM-dd'));
  const [showPreview, setShowPreview] = useState(false);

  // Helper function to generate date range string for file names
  const getDateRangeString = () => {
    switch (downloadRange) {
      case 'current-month':
        return format(currentDisplayMonth, 'yyyy-MM');
      case 'next-month':
        return format(addMonths(currentDisplayMonth, 1), 'yyyy-MM');
      case 'custom-range':
        return `${format(new Date(customStartDate), 'yyyy-MM-dd')}_to_${format(new Date(customEndDate), 'yyyy-MM-dd')}`;
      case 'all-data':
        return 'all-data';
      default:
        return format(currentDisplayMonth, 'yyyy-MM');
    }
  };

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      // Find the calendar element to capture
      const calendarElement = calendarRef?.current || document.querySelector('[data-calendar-container]');
      
      if (!calendarElement) {
        toast.error("Calendar not found for PDF generation");
        return;
      }

      // Generate canvas from the calendar element with balanced settings for print quality and file size
      const canvas = await html2canvas(calendarElement as HTMLElement, {
        scale: 2, // Higher scale for better quality, but we'll optimize compression instead
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        backgroundColor: '#ffffff', // Ensure white background for printing
        removeContainer: true,
        imageTimeout: 0,
        allowTaint: false,
        width: calendarElement.scrollWidth,
        height: calendarElement.scrollHeight
      });

      // Create PDF optimized for printing
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true // Enable PDF compression
      });

      // Convert canvas to JPEG with better quality balance (90% for good print quality)
      const imgData = canvas.toDataURL('image/jpeg', 0.9); // 90% quality for better print result
      
      // Add header with business branding
      pdf.setFontSize(16); // Slightly smaller for better proportion
      pdf.setTextColor(0, 0, 0); // Pure black for better printing
      pdf.text(`${businessName} - Shift Schedule`, 15, 15);
      
      pdf.setFontSize(12);
      pdf.setTextColor(60, 60, 60);
      const dateRange = getDateRangeDisplayText();
      pdf.text(dateRange, 15, 25);
      
      // Add generation timestamp
      pdf.setFontSize(8);
      pdf.text(`Generated: ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, 15, 30);

      // Calculate optimal image sizing for printing
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const maxImgWidth = pdfWidth - 30; // 15mm margins
      const maxImgHeight = pdfHeight - 50; // Leave space for header and margin
      const startY = 35;

      // Calculate aspect ratio preserving dimensions
      const imgAspectRatio = canvas.width / canvas.height;
      let imgWidth = maxImgWidth;
      let imgHeight = imgWidth / imgAspectRatio;

      // If height exceeds page, scale by height instead
      if (imgHeight > maxImgHeight) {
        imgHeight = maxImgHeight;
        imgWidth = imgHeight * imgAspectRatio;
      }

      // Center the image
      const xOffset = (pdfWidth - imgWidth) / 2;
      
      // Add the optimized image
      pdf.addImage(imgData, 'JPEG', xOffset, startY, imgWidth, imgHeight);

      // Save the PDF
      const fileName = `${(businessName || 'schedule').toLowerCase().replace(/\s+/g, '-')}-shifts-${getDateRangeString()}.pdf`;
      pdf.save(fileName);

      toast.success("Printable PDF generated successfully");

      setIsOpen(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Helper function to get display text for date range
  const getDateRangeDisplayText = () => {
    switch (downloadRange) {
      case 'current-month':
        return format(currentDisplayMonth, 'MMMM yyyy');
      case 'next-month':
        return format(addMonths(currentDisplayMonth, 1), 'MMMM yyyy');
      case 'custom-range':
        return `${format(new Date(customStartDate), 'MMM dd, yyyy')} - ${format(new Date(customEndDate), 'MMM dd, yyyy')}`;
      case 'all-data':
        return 'All Schedule Data';
      default:
        return format(currentDisplayMonth, 'MMMM yyyy');
    }
  };

  // Get actual date range for filtering shifts
  const getFilterDateRange = () => {
    switch (downloadRange) {
      case 'current-month':
        return {
          startDate: startOfMonth(currentDisplayMonth),
          endDate: endOfMonth(currentDisplayMonth)
        };
      case 'next-month': {
        const nextMonth = addMonths(currentDisplayMonth, 1);
        return {
          startDate: startOfMonth(nextMonth),
          endDate: endOfMonth(nextMonth)
        };
      }
      case 'custom-range':
        return {
          startDate: new Date(customStartDate),
          endDate: new Date(customEndDate)
        };
      case 'all-data':
      default:
        return {
          startDate: new Date(0), // Beginning of time
          endDate: new Date(2099, 11, 31) // Far future
        };
    }
  };

  // Generate preview of what will be included
  const getPreviewInfo = () => {
    const { startDate, endDate } = getFilterDateRange();
    
    if (downloadRange === 'all-data') {
      return {
        dateRange: 'All available data',
        totalShifts: shifts?.length || 0,
        employeesCount: new Set(shifts?.map(s => s.employeeId)).size || 0
      };
    }

    const filteredShifts = shifts?.filter(shift => {
      if (!shift.startTime) return false;
      const shiftDate = new Date(shift.startTime);
      return isWithinInterval(shiftDate, { start: startDate, end: endDate });
    }) || [];

    return {
      dateRange: getDateRangeDisplayText(),
      totalShifts: filteredShifts.length,
      employeesCount: new Set(filteredShifts.map(s => s.employeeId)).size
    };
  };

  const handleSendEmail = async () => {
    setIsSendingEmail(true);
    
    try {
      // Validate email recipients
      const recipients = emailData.recipients.split(',').map(email => email.trim()).filter(Boolean);
      if (recipients.length === 0) {
        toast.error("Please enter at least one email recipient");
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = recipients.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        toast.error("Invalid email addresses", {
          description: invalidEmails.join(', ')
        });
        return;
      }

      // Call Supabase Edge Function to send email
      const { data, error } = await supabase.functions.invoke('send-calendar-email', {
        body: {
          recipients,
          subject: emailData.subject,
          message: emailData.message,
          businessName: businessName || 'Your Business',
          scheduleMonth: format(currentDisplayMonth, 'MMMM yyyy'),
          scheduleData: {
            // You can add more schedule data here if available
            totalShifts: 0, // This would come from your shift data
            totalEmployees: 0, // This would come from your employee data
            coverageDays: 0 // This would be calculated from your schedule
          },
          senderEmail: `shifts@${businessName?.toLowerCase().replace(/\s+/g, '') || 'business'}.com`
        }
      });

      if (error) {
        throw error;
      }

      toast.success(data.message || `Schedule sent to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`);

      setIsOpen(false);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error instanceof Error ? error.message : String(error) || "Failed to send email");
    } finally {
      setIsSendingEmail(false);
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Share className="mr-2 h-4 w-4" />
            Share
          </Button>
        )}
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Calendar</DialogTitle>
          <DialogDescription>
            Export, email, or sync your shift schedule with others
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Download Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Download className="h-4 w-4" />
              <h3 className="font-semibold">Download</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Generate a printable PDF of your shift schedule
            </p>
            
            {/* Date Range Selection */}
            <div className="space-y-3 mb-4">
              <Label className="text-sm font-medium">Date Range</Label>
              <Select value={downloadRange} onValueChange={(value: 'current-month' | 'next-month' | 'custom-range' | 'all-data') => setDownloadRange(value)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current-month">Current Month ({format(currentDisplayMonth, 'MMM yyyy')})</SelectItem>
                  <SelectItem value="next-month">Next Month ({format(addMonths(currentDisplayMonth, 1), 'MMM yyyy')})</SelectItem>
                  <SelectItem value="custom-range">Custom Range</SelectItem>
                  <SelectItem value="all-data">All Data</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Custom Date Range Inputs */}
              {downloadRange === 'custom-range' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="start-date" className="text-xs">From</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-date" className="text-xs">To</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Preview Section */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview</h4>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  {showPreview ? 'Hide' : 'Show'} Details
                </button>
              </div>
              
              {(() => {
                const previewInfo = getPreviewInfo();
                return (
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Date Range:</span> {previewInfo.dateRange}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Shifts:</span> {previewInfo.totalShifts}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Employees:</span> {previewInfo.employeesCount}
                    </p>
                    
                    {showPreview && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          The PDF will include the calendar view for the selected date range with all scheduled shifts, 
                          optimized for A4 landscape printing.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            
            <Button 
              onClick={handleGeneratePDF} 
              disabled={isGeneratingPDF}
              className="w-full justify-center"
              size="lg"
            >
              <FileText className="mr-2 h-4 w-4" />
              {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF for Printing'}
            </Button>
          </div>

          {/* Email Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="h-4 w-4" />
              <h3 className="font-semibold">Email Schedule</h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <Label htmlFor="recipients" className="text-sm">Email Recipients</Label>
                <Input
                  id="recipients"
                  placeholder="email1@example.com, email2@example.com"
                  value={emailData.recipients}
                  onChange={(e) => setEmailData(prev => ({ ...prev, recipients: e.target.value }))}
                  className="text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Separate with commas</p>
              </div>
              
              <div>
                <Label htmlFor="subject" className="text-sm">Subject</Label>
                <Input
                  id="subject"
                  value={emailData.subject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  className="text-sm"
                />
              </div>
              
              <div>
                <Label htmlFor="message" className="text-sm">Message</Label>
                <Textarea
                  id="message"
                  rows={3}
                  value={emailData.message}
                  onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                  className="text-sm"
                />
              </div>
              
              <Button 
                onClick={handleSendEmail} 
                disabled={isSendingEmail || !emailData.recipients}
                className="w-full"
              >
                {isSendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>

          {/* Sync Calendar Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4" />
              <h3 className="font-semibold">Integration</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Sync your shifts with external calendars
            </p>
            
            {onSyncCalendar && (
              <Button 
                onClick={onSyncCalendar} 
                variant="outline"
                className="w-full justify-start"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Sync with Google Calendar
              </Button>
            )}
            
            <div className="text-xs text-gray-500 mt-4">
              <p>More integrations coming soon...</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareCalendarDialog;